import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import DropDown from "src/Components/DropDown/DropDown";
import { InfoMessageType } from "src/Components/InfoBar/InfoBar";
import SmallDialog from "src/Components/Dialog/SmallDialog";
import { ClinicRoles } from "src/Utils/Hooks/GetClinicInfo/useGetClinicInfo";
import { IClinicUser, UserStatus } from "src/Utils/Hooks/GetClinicInfo/useGetUserDetails";
import { AdminRoleOPeration, useGetManageUserRoles } from "src/Utils/Hooks/ManageUserRoles/ManageUserRoles";
import { useTransferOwnership } from "src/Utils/Hooks/ManageUserRoles/useTransferOwnership";
import { logger } from "src/Utils/LoggerService/LoggerService";
import { useClinicContext } from "src/Utils/Hooks/ClinicContext";

export interface IUserRoles {
    user: IClinicUser;
    setInfo: (message: string, type: InfoMessageType) => void;
    updatePageLoader: (load: boolean) => void;
};


// Full display name for messages/dialogs; falls back to email for users who have not
// completed registration (and so have no first/last name yet).
const getDisplayName = (user: IClinicUser) =>
    [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

const UpdateUserRole: React.FC<IUserRoles> = ({ user, setInfo, updatePageLoader }) => {
    const { updateRoles, manageRolesStatus, manageRolesError, manageRolesLoading } = useGetManageUserRoles()
    const { transferOwnership, transferStatus, transferError, transferLoading } = useTransferOwnership()
    const { t } = useTranslation();
    const { currentUser } = useClinicContext()
    const [selectedUserId, setSelectedUserId] = useState('')
    const [showTransferConfirm, setShowTransferConfirm] = useState(false)
    const reloadScheduled = useRef(false)
    const reloadTimer = useRef<ReturnType<typeof setTimeout>>()
    const ownerRole = "Owner";
    const defaultRole = "Member";
    const adminRole = "Admin";

    const isOwner = (roles: string[]) => roles.indexOf(ClinicRoles.ClinicOwner) >= 0;
    const isAdmin = (roles: string[]) => roles.indexOf(ClinicRoles.ClinicAdmin) >= 0;

    const currentRole = isOwner(user.roles) ? ownerRole : isAdmin(user.roles) ? adminRole : defaultRole;

    // You cannot change your own role (no self-promotion/demotion, no transfer to self).
    const isSelf = currentUser?.clinicUser?.userId === user.userId;

    // A clinic owner can transfer ownership only to another Admin. The target must be a
    // registered admin and not yourself; the Owner option is not offered on Member rows.
    const isRegistered = user.lifecycle?.status === UserStatus.Created;
    const canTransferToUser = (currentUser?.isClinicOwner ?? false) && isRegistered && !isSelf && isAdmin(user.roles);
    const userRoles: string[] = canTransferToUser ? [defaultRole, adminRole, ownerRole] : [defaultRole, adminRole];

    // The dropdown is disabled when: the viewer isn't a clinic admin, the row is the owner
    // (owner role changes only via transfer), or the row is the viewer's own account.
    const dropdownDisabled = !currentUser?.isClinicAdmin || isOwner(user.roles) || isSelf;

    const onRoleChange = useCallback((user: IClinicUser, item: string) => {
        setSelectedUserId(getDisplayName(user))
        if (item === ownerRole) {
            setShowTransferConfirm(true)
            return;
        }
        updateRoles({
            clinicId: user.clinicId,
            email: user.email,
            roleOperation: item === defaultRole ? AdminRoleOPeration.Remove : AdminRoleOPeration.Add
        }
        )
    }, [updateRoles])

    const onConfirmTransfer = useCallback(() => {
        setShowTransferConfirm(false)
        transferOwnership({ clinicId: user.clinicId, userId: user.userId })
    }, [transferOwnership, user.clinicId, user.userId])

    useEffect(() => {
        if (manageRolesStatus) {
            let successMessage = t('RoleUpdateSuccessMsg', {selectedUserId: selectedUserId} )
            logger.logInformation(successMessage);
            setInfo(successMessage, InfoMessageType.success);
        }
        if (manageRolesError !== '') {
            logger.logError(`Failed to update ${selectedUserId} Role. Error occured: ${manageRolesError}`)
            setInfo(manageRolesError, InfoMessageType.error)
        }
    }, [manageRolesStatus, manageRolesError, selectedUserId, setInfo, t])

    useEffect(() => {
        if (transferError !== '') {
            logger.logError(`Failed to transfer ownership to ${selectedUserId}. Error occured: ${transferError}`)
            setInfo(transferError, InfoMessageType.error)
            return;
        }
        if (transferStatus && !reloadScheduled.current) {
            reloadScheduled.current = true;
            let successMessage = t('OwnershipTransferSuccessMsg', {selectedUserId: selectedUserId})
            logger.logInformation(successMessage);
            setInfo(successMessage, InfoMessageType.success);
        }
    }, [transferStatus, transferError, selectedUserId, setInfo, t])

    // Clear any pending reload timer if the component unmounts before it fires.
    useEffect(() => () => {
        if (reloadTimer.current) {
            clearTimeout(reloadTimer.current);
        }
    }, [])

    useEffect(()=> {
        updatePageLoader(manageRolesLoading || transferLoading);
    },[manageRolesLoading, transferLoading, updatePageLoader])

    return (
        <div style={{display: 'flex'}}>
             {showTransferConfirm &&
                <SmallDialog
                    title={t('OwnershipTransfer')}
                    submitButtonText={t('TransferOwnership')}
                    width='min(492px, 90vw)'
                    fullWidthButtons
                    onClose={() => setShowTransferConfirm(false)}
                    onSubmit={onConfirmTransfer}>
                    <div className="mt-2">
                        <p>
                            <Trans i18nKey='TransferOwnershipConfirm' values={{userName: getDisplayName(user)}}/>
                        </p>
                        <ul className="mt-3" style={{ paddingLeft: '1.2rem' }}>
                            <li className="mb-2"><Trans i18nKey='TransferOwnershipPointSingleOwner'/></li>
                            <li className="mb-2"><Trans i18nKey='TransferOwnershipPointSubscription' values={{userName: getDisplayName(user)}}/></li>
                            <li><Trans i18nKey='TransferOwnershipPointBecomeAdmin'/></li>
                        </ul>
                    </div>
                </SmallDialog>
             }
             <DropDown
             disabled={dropdownDisabled}
             items={userRoles}
             selectedItem={currentRole}
             onSelectionChange={(item) => onRoleChange(user, item)}
             />
        </div>
    );
};

export default UpdateUserRole;
